import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { STATES } from 'src/app/shared/models/states';
import { AppointmentService } from 'src/app/shared/services/appointment/appointment.service';
import { AppointmentSearchResult } from 'src/app/shared/models/callzone-appointment';
import { UserProfile } from 'src/app/shared/models/user-profile';
import { Subscription, Observable } from 'rxjs';
import { UserProfileService } from 'src/app/shared/services/user/user-profile.service.service';
import { CallZoneApiService } from 'src/app/shared/services/api/callzone/callzone-api.service';
import { DetailOptions } from 'src/app/shared/models/detail-options';
import { ApplicationService } from 'src/app/shared/services/application/application.service';
import { CallStatusData } from './call-status';

@Component({
    selector: 'app-search-appointments',
    templateUrl: './search-appointments.component.html',
    styleUrls: ['./search-appointments.component.scss'],
    standalone: false
})
export class SearchAppointmentsComponent implements OnInit {
  searchForm: UntypedFormGroup;
  isSearch: boolean;
  states: any;
  status = CallStatusData;
  pageSize = 10;
  page = 0;
  appointmentList;
  previousPage: number;
  pageNumber = 1;
  totalNoOfRecords: number;
  timeZone: string;
  subscription: Subscription;
  selectedValue: string;
  openModal = false;
  config: DetailOptions = {
    canStartVerification: false,
    canAddNotes: true,
  };
  sortOrder = true;
  sortColumn = 'ScheduledDateUTC';
  isReset: boolean;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private appointmentService: AppointmentService,
    private SpinnerService: NgxSpinnerService,
    private profileService: UserProfileService,
    public callzoneService: CallZoneApiService,
    public applicationService: ApplicationService
  ) {}

  ngOnInit() {
    this.getProfileDetails();
    this.initForm();
    this.isSearch = false;
    this.getQueryparamsAndSearch();
    this.formValueChanges();
    this.isReset = localStorage.getItem('isReset') === 'true';
    if (this.isReset) {
      this.clearSearchData();
      localStorage.removeItem('isReset');
    }
  }

  clearSearchData() {
    this.searchForm.reset();
    this.isSearch = false;
  }

  getQueryparamsAndSearch() {
    const uriParams = this.route.snapshot.queryParams;
    if (Object.keys(uriParams).length > 0) {
      this.searchForm.patchValue({
        applicationId: uriParams.applicationId || '',
        firstName: uriParams.applicantFirstName || '',
        lastName: uriParams.applicantLastName || '',
        state: uriParams.state || '',
        policyNumber: uriParams.policyNumber || '',
      });
      this.searchApplications();
    }
  }

  initForm() {
    this.searchForm = new UntypedFormGroup(
      {
        applicationId: new UntypedFormControl('', Validators.pattern('[0-9]+')),
        firstName: new UntypedFormControl(''),
        lastName: new UntypedFormControl(''),
        policyNumber: new UntypedFormControl(''),
        state: new UntypedFormControl(''),
      },
      this.atLeastOneInputHasValue()
    );
  }

  // not displaying message, but making form-invalid
  // & disable button if atleast one value available for search
  atLeastOneInputHasValue(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const formGroup = control as FormGroup;
      const controls = formGroup.controls;
      for (const key in controls) {
        if (controls[key].value) {
          return null;
        }
      }
      return { atLeastOneRequired: true };
    };
  }

  /**
   * Subscribes to the value changes of form and sets error to the form if all the inputs are empty
   */
  formValueChanges() {
    this.searchForm.valueChanges.subscribe((values) => {
      const emptyValue = Object.keys(this.searchForm.value).every(
        (eachFormValue) => !this.searchForm.value[eachFormValue]
      );
      if (emptyValue) {
        this.searchForm.setErrors({ emptyInputs: true });
      } else {
        this.searchForm.setErrors(null);
      }
    });
  }

  navigate(url, appId, tabName, item) {
    if (url) {
      !tabName
        ? window.open(`${url}/${appId}`, '_blank')
        : window.open(`${url}/${appId}?tabName=${tabName}`);
    } else {
      const applicationId = item.applicationId.toString();
      this.applicationService.SelectApplication(applicationId);
      this.appointmentService.SelectAppointment(item);
      this.openModal = true;
    }
  }

  getProfileDetails() {
    this.timeZone = this.profileService.getCallzoneUser()?.timeZone;
  }

  loadPage(page: number) {
    this.pageNumber = page;
    if (page !== this.previousPage) {
      this.previousPage = page;
      this.searchApplications();
    }
  }

  // sorting appointments with respective column
  sort(columnName, eve) {
    this.sortColumn = columnName;
    this.sortOrder = !this.sortOrder;
    const element = eve.target;
    const sortType =
      element.className.includes('active') && this.sortOrder ? 'ASC' : 'DESC';
    this.searchForm.value['SortBy'] = this.sortColumn + ' ' + sortType;
    this.searchApplications();
  }

  searchApplications() {
    if (this.searchForm.valid) {
      this.isSearch = true;
      this.SpinnerService.show();
      // search Query
      const appointmentSearchRequest = this.appointmentSearchRequest(
        this.searchForm.value,
        true
      );
      const search = this.appointmentSearchQuery(appointmentSearchRequest);
      // If sorting with column name with sortType ex: "SortBy: 'statusName DESC'"
      if (this.searchForm.value.hasOwnProperty('SortBy')) {
        search['SortBy'] = this.searchForm.value['SortBy'];
      } else {
        delete search['SortBy'];
      }
      // for setting the query params
      const appointmentQueryParams = this.appointmentSearchRequest(
        this.searchForm.value,
        false
      );
      const searchQuery = this.appointmentSearchQuery(appointmentQueryParams);
      // navigation with all the parameters
      const { ...fields }: any = searchQuery;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: fields,
      });
      // api call
      if (Object.keys(search).length !== 0) {
        this.appointmentService
          .SearchAppointments(search)
          .then((data: AppointmentSearchResult) => {
            if (data) {
              this.SpinnerService.hide();
              this.appointmentList = data.items;
              this.appointmentList.map((item) => {
                if (item.statusName === 'TODO') {
                  item.statusName = 'To Do';
                }
              });
              this.totalNoOfRecords = data.metaData.numberOfRecords;
            }
          });
      } else {
        this.SpinnerService.hide();
        this.isSearch = false;
      }
    }
  }

  appointmentSearchQuery(appointmentSearchRequest) {
    let search = Object.keys(appointmentSearchRequest).reduce((acc, el) => {
      // excluding empty valued parameters before passing to api
      if (appointmentSearchRequest[el] !== '') {
        acc[el] = appointmentSearchRequest[el];
      }
      if (acc['applicationId'] === 0) {
        delete acc['applicationId'];
      }
      return acc;
    }, {});
    if (Object.keys(search).length <= 2) {
      search = {};
    }
    return search;
  }

  appointmentSearchRequest(formData, isQueryParams: boolean) {
    const appointmentSearchRequest = {
      applicationId: Number((formData.applicationId || '').toString().trim()),
      applicantFirstName: (formData.firstName || '').trim(),
      applicantLastName: (formData.lastName || '').trim(),
      state: (formData.state || '').trim(),
      pageNumber: this.pageNumber - 1,
      pageSize: this.pageSize,
      policyNumber: (formData.policyNumber || '').trim(),
    };
    return appointmentSearchRequest;
  }
  getNotifyEvent() {
    this.openModal = false;
    this.appointmentService.Clear();
    this.applicationService.Clear();
  }

  resetForm() {
    localStorage.setItem('isReset', 'true');
    this.searchForm.reset();
    this.isSearch = false;
  }
}
